/* eslint no-loops/no-loops: 0, no-plusplus: 0 */

import queryString from "query-string";

import {
	FIELDS,
	AGE_MIN,
	AGE_MAX,
	DISTANCE_CUTOFF,
	DISTANCE_OPTIONS,
	FIELDS_VALUE_MAP,
	FEEDBACK,
	LOOKINGFOR_FIELDS_MAP,
	GENDER_TAGS_LENGTH,
	LOOKINGFOR_DATA_LENGTH,
	LOOKINGFOR_VALUES_MAP,
	LOOKINGFOR_FIELDS_ORDER,
	NON_MONOGAMOUS,
} from "./constants";
import pickOne from "shared/util/pickOne";
import _strf from "shared/util/_strf";
import _str from "shared/util/_str";

function _isInputPresent(input) {
	if (!input) {
		return false;
	}

	if (Array.isArray(input)) {
		return !!input.length;
	} else {
		return !!`${input}`.length;
	}
}

export function isFieldValid(field, fieldValues) {
	const inputName = FIELDS_VALUE_MAP[field];
	let fieldData = fieldValues[inputName];

	if (inputName === "lookingfor") { // monogamy is not a relationship type
		fieldData = fieldData
			.filter((relationshipType) => relationshipType !== NON_MONOGAMOUS);
	}

	if (
		typeof inputName === "object" &&
		!Array.isArray(inputName)
	) {
		// if the current field data is a part of a multi-input field
		// check all inputs before assigning validity
		return Object.keys(inputName).every((input) => {
			return _isInputPresent(fieldValues[inputName[input]]);
		});
	} else {
		return _isInputPresent(fieldData);
	}
}

export function makeAgeRange(placeholder, min, max) {
	const ages = [];

	// placeholder for null value. Ex: Min
	ages.push({ value: "", name: placeholder, disabled: true });

	/* eslint-disable no-loops/no-loops */

	for (let i = min; i <= max; i++) {
		ages.push({ value: i, name: i });
	}

	/* eslint-disable no-loops/no-loops */

	return ages;
}

export function makeDistanceRange(unit, entryPoint) {
	let options = DISTANCE_OPTIONS;
	const res = [];

	// if we are in onboarding, we want to make 25 the minimum disatance
	if (entryPoint === "onboarding") {
		options = options.filter((option) => {
			return parseInt(option, 10) >= 25;
		});
	}

	// placeholder for null value.
	res.push({ value: "", name: "Max distance", disabled: true });

	options.forEach((option) => {
		res.push({ value: option, name: `Within ${option} ${unit}` });
	});

	// add in anywhere as the last option
	res.push({ value: "anywhere", name: "Anywhere" });

	return res;
}

export function _findObjectIndex(array, property, targetValue) {
	const arrayLength = array.length;

	for (let i = 0; i < arrayLength; i++) {
		if (array[i][property] === targetValue) {
			return i;
		}
	}

	return -1;
}

export function makeGenderList(genders) {
	const gendersCopy = [...genders];
	const genderList = [];

	// make sure man is the first value and woman is the second
	const indexOfMan = _findObjectIndex(genders, "name", "Man");
	const indexOfWoman = _findObjectIndex(genders, "name", "Woman");

	if (indexOfMan !== -1 && indexOfWoman !== -1) {
		genderList.push(
			{
				value: genders[indexOfMan].id,
				name: genders[indexOfMan].name_plural ?
					genders[indexOfMan].name_plural : genders[indexOfMan].name,
			},
			{
				value: genders[indexOfWoman].id,
				name: genders[indexOfWoman].name_plural ?
					genders[indexOfWoman].name_plural : genders[indexOfWoman].name,
			},
		);

		gendersCopy.splice(indexOfMan, 1);
		gendersCopy.splice(indexOfWoman, 1);
	}

	const gendersCount = gendersCopy.length;

	/* eslint-disable no-loops/no-loops */

	for (let i = 0; i < gendersCount; i++) {
		genderList.push({
			value: gendersCopy[i].id,
			name: gendersCopy[i].name_plural ? gendersCopy[i].name_plural : gendersCopy[i].name,
		});
	}

	/* eslint-disable no-loops/no-loops */

	return genderList;
}

// this needs to know the current field, the last inputed data and the length of values
export function getFeedback(currentField, input, previousInput, apiData) {
	let feedback = null;

	if (!input) {
		return feedback;
	}

	const fieldFeedback = FEEDBACK[currentField];

	switch (currentField) {
		case FIELDS.LOOKINGFOR: {
			const inputLength = input.length;

			// check if they've just unselected a value
			if (inputLength < previousInput.length) {
				feedback = null;
			} else if (inputLength === apiData.length - 1) {
				feedback = pickOne(fieldFeedback.all);
			} else if (fieldFeedback[input[inputLength - 1]]) {
				feedback = pickOne(fieldFeedback[input[inputLength - 1]]);
			}

			return feedback;
		}

		case FIELDS.DISTANCE: {
			const inputValue = parseInt(input, 10);

			if (Number.isNaN(inputValue) && input !== "anywhere") {
				feedback = null;
			} else if (inputValue <= DISTANCE_CUTOFF) {
				feedback = pickOne(fieldFeedback.close);
			} else {
				feedback = pickOne(fieldFeedback.far);
			}

			return feedback;
		}

		case FIELDS.AGE: {
			const { ageMax, ageMin } = input;

			// if they haven't filled out all inputs yet, no feedback
			if (!ageMax || !ageMin) {
				feedback = null;
			} else if (ageMax === ageMin) {
				feedback = _strf(pickOne(fieldFeedback.same), { age: `${ageMin}-year-olds` });
			} else if (ageMin === AGE_MIN && ageMax === AGE_MAX) {
				feedback = pickOne(fieldFeedback.min_and_max);
			} else if (ageMax - ageMin > 20) {
				feedback = pickOne(fieldFeedback.range);
			}

			return feedback;
		}

		default: {
			return feedback;
		}
	}
}

export function makeSentence(array, sortOrder = false) {
	// joins items in array with commas and adds an `and` before the last item
	if (!array || !array.length) {
		return "";
	}

	let sentence;
	let arrayCopy = [...array];
	const arrayLength = array.length;

	if (sortOrder) {
		arrayCopy = [];

		sortOrder.forEach((field) => {
			if (array.indexOf(field) >= 0) {
				arrayCopy.push(field);
			}
		});
	}

	if (arrayLength === 2) {
		const firstItem = arrayCopy[0];
		const lastItem = arrayCopy[1];

		sentence = `${firstItem} and ${lastItem}`;
	} else if (arrayLength > 2) {
		const subArray = arrayCopy.slice(0, arrayLength - 1);
		const lastItem = arrayCopy[arrayLength - 1];

		sentence = `${subArray.join(", ")}, and ${lastItem}`;
	} else {
		sentence = arrayCopy[0];
	}

	return _str(sentence.replace(/_/gi, " "));
}

export function getDataSentence(field, data, dataMap) {
	switch (field) {
		case FIELDS.AGE: {
			if (!data.ageMin || !data.ageMax) {
				return null;
			}

			if (data.ageMin === data.ageMax) {
				return _strf("$age years old", { age: data.ageMin });
			} else {
				return _strf("$ageMin to $ageMax years old", { ageMin: data.ageMin, ageMax: data.ageMax });
			}
		}

		case FIELDS.DISTANCE: {
			if (!data.distance) {
				return null;
			}

			if (data.distance === "anywhere") {
				return _str("anywhere");
			} else {
				const unit = data.isMetric ? "kilometers" : "miles";

				return _strf("within $distance $unit", { distance: data.distance, unit });
			}
		}

		case FIELDS.GENDER: {
			if (!data.genders) {
				return null;
			}

			if (data.genders.length === GENDER_TAGS_LENGTH) {
				return _str("everyone");
			}

			let res = data.genders;

			// if we have more detailed gender information, convert genders from
			// singular to plural
			if (typeof dataMap !== "undefined" && Object.keys(dataMap).length) {
				res = res.map((gender) => {
					const genderInfo = dataMap.filter((mapOption) => {
						return mapOption.id === gender;
					})[0];

					if (genderInfo && genderInfo.name_plural) {
						return genderInfo.name_plural.toLowerCase();
					} else {
						return gender;
					}
				});
			}

			return _str(makeSentence(res));
		}

		case FIELDS.LOOKINGFOR: {
			if (!data.lookingfor) {
				return null;
			}

			const lookingforData = [...data.lookingfor].filter((relationship) => {
				return relationship !== "&mdash;" && relationship !== NON_MONOGAMOUS;
			});

			if (lookingforData.length === LOOKINGFOR_DATA_LENGTH) {
				return _str("all types");
			}

			const shortTermIndex = lookingforData.indexOf(LOOKINGFOR_FIELDS_MAP.SHORT_TERM_DATING);
			const longTermIndex = lookingforData.indexOf(LOOKINGFOR_FIELDS_MAP.LONG_TERM_DATING);

			if (shortTermIndex !== -1 && longTermIndex !== -1) {
				lookingforData.splice(shortTermIndex, 1);
				lookingforData.splice(lookingforData.indexOf(LOOKINGFOR_FIELDS_MAP.LONG_TERM_DATING), 1);

				lookingforData.push(LOOKINGFOR_VALUES_MAP.short_and_long_term);
			}

			const casualSexIndex = lookingforData.indexOf(LOOKINGFOR_FIELDS_MAP.CASUAL_SEX);

			if (casualSexIndex !== -1) {
				lookingforData.splice(casualSexIndex, 1);

				lookingforData.push(LOOKINGFOR_VALUES_MAP.casual_sex);
			}

			return _str(makeSentence(lookingforData, LOOKINGFOR_FIELDS_ORDER));
		}

		default: {
			return null;
		}
	}
}

export function calculateDistance(distance, isMetric) {
	if (distance === null) {
		return "";
	} else if (distance === 0) {
		return "anywhere";
	} else if (isMetric) {
		// distance is stored in miles. Switch it back to km
		// round it up to the closet 5
		return Math.ceil(distance / 0.621371 / 5) * 5;
	} else {
		return distance;
	}
}

/**
 * Check if a user is in the non-monogamy flavor of reboarding.
 * @return {bool} true if non-monog reboarding
 */
export const isNonMonogamyReboarding = () => {
	if (!window || !window.location || !window.location.search) {
		return false;
	}

	const queryParams = queryString.parse(window.location.search);
	return !!(queryParams.needs_reboarding && queryParams.non_monogamy_reboarding);
};



// WEBPACK FOOTER //
// ./src/apps/what_i_want/util/helpers.js